import React from 'react';

/* Enemy-side status vocabulary — the mirror of the player-side TAG_META
   (CombatPlayerbar/statusTags.jsx), but for ENEMY-controlled state: gauges,
   charges, timers, phases, spawns, shields and class-matches. Deliberately kept
   SEPARATE from TAG_META, which means "player-carried status" and drives the
   "Inflicts [chip]" decode grammar. Enemy state is never "inflicted on the
   player," so it gets its own verbs (Runs / Charges / Drains / Counts down / Cycles /
   Spawns / Shielded / Accepts / Rebuilds) and its own registry.

   Ratified design: memory/enemy-side-status-bar-feature.md (2026-07-13). The
   backend surfaces a normalized payload per enemy:
     statuses: [{ key, value, max?, zone?, label? }]
   Every value is the CURRENT post-round/post-action value; `zone` comes from the
   backend threshold logic (never recomputed here). Honesty floor: a chip only
   exists when the engine tracks real state — `NONE` enemies surface `[]`.

   This module resolves each payload row to a renderer TYPE + chip chrome. It is
   robust to keys it doesn't know yet: an unknown key infers its type from the
   payload shape and falls back to the payload's own `label`. Add precise
   ENEMY_STATUS_META entries as the backend key set settles. */

const svg = (children) => function EnemyStatusGlyph({ size = 14, className }) {
  return (
    <svg
      width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round"
      strokeLinejoin="round" aria-hidden="true" className={className}
    >{children}</svg>
  );
};

// One glyph per renderer type. Monochrome, currentColor, 24-grid — matches the
// MECH glyph set so enemy chips read on the same visual key as player chips.
const GaugeIcon = svg(<><path d="M4 15a8 8 0 0 1 16 0" /><path d="M12 15l4-3" /><circle cx="12" cy="15" r="1.2" fill="currentColor" /></>);
const ChargeIcon = svg(<path d="M13 2.5 5.5 13.5H11l-1.2 8 7.7-11.5H12l1-7.5z" />);
const TimerIcon = svg(<><circle cx="12" cy="13.5" r="7.5" /><path d="M12 9.5v4l2.8 1.8" /><path d="M9 3.5h6" /></>);
const PhaseIcon = svg(<path d="M2.5 12h3.5l2 6 4-14 2.2 8H21.5" />);
const SpawnIcon = svg(<><circle cx="8" cy="8.5" r="3.3" /><circle cx="16" cy="15.5" r="3.3" /><path d="M10.4 10.7l3.2 2.6" /></>);
const ShieldIcon = svg(<path d="M12 3l7 3v5c0 4.6-3 8.1-7 10-4-1.9-7-5.4-7-10V6z" />);
const MatchIcon = svg(<><rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></>);
// A regen loop-arrow for a defended setpoint that climbs back each round.
const RebuildIcon = svg(<><path d="M20 12a8 8 0 1 1-2.3-5.6" /><path d="M20 3.5v4h-4" /></>);
const DiuresisIcon = svg(<path d="M12 2.8S5.8 9.6 5.8 14.3a6.2 6.2 0 0 0 12.4 0C18.2 9.6 12 2.8 12 2.8z" />);

/* The reusable renderer types. `statusClass` drives the CSS chip modifier
   (bls-status-header--enemy-gauge, ...); `color` is the left-stripe + icon tint. */
export const ENEMY_STATUS_TYPES = {
  'enemy-gauge':  { statusClass: 'enemy-gauge',  color: 'rgb(45, 212, 191)',  Icon: GaugeIcon,  verb: 'Runs',      line: 'A live enemy-controlled clinical range.' },
  'enemy-charge': { statusClass: 'enemy-charge', color: 'rgb(251, 146, 60)',  Icon: ChargeIcon, verb: 'Charges',   line: 'Builds toward a threshold event.' },
  // ⚠️ The verb is the TYPE's, not one member's. It read 'Divides in' while division
  // was the only timer; `punctal-counseling` (below) is a second, non-dividing one,
  // and the verb surfaces as the compendium's ENEMY · TIMER folder sublabel
  // (compendiumData.js:975, mechanics.jsx:923), where a division-specific word would
  // now be false. Division's own chip LABEL is unchanged and still reads "Divides in".
  'enemy-timer':  { statusClass: 'enemy-timer',  color: 'rgb(56, 189, 248)',  Icon: TimerIcon,  verb: 'Counts down', line: 'Counts down to an enemy event.' },
  'enemy-phase':  { statusClass: 'enemy-phase',  color: 'rgb(167, 139, 250)', Icon: PhaseIcon,  verb: 'Cycles',    line: 'The enemy cycles through named combat states.' },
  'enemy-spawn':  { statusClass: 'enemy-spawn',  color: 'rgb(244, 114, 182)', Icon: SpawnIcon,  verb: 'Spawns',    line: 'A tracked condition is approaching a spawn.' },
  'enemy-shield': { statusClass: 'enemy-shield', color: 'rgb(148, 163, 184)', Icon: ShieldIcon, verb: 'Shielded',  line: 'Incoming damage is zero while the stated condition holds.' },
  'enemy-match':  { statusClass: 'enemy-match',  color: 'rgb(96, 165, 250)',  Icon: MatchIcon,  verb: 'Accepts',   line: 'Only the listed therapeutic classes produce the stated effect.' },
  // A defended setpoint the enemy heals back toward every round (Ten Shin's
  // resistant hypertension) — the player's drain must outpace the rebuild.
  'enemy-rebuild': { statusClass: 'enemy-rebuild', color: 'rgb(239, 68, 68)', Icon: RebuildIcon, verb: 'Rebuilds', line: 'A defended setpoint the enemy heals back toward each round.' },
  'enemy-diuresis': { statusClass: 'enemy-diuresis', color: 'rgb(56, 189, 248)', Icon: DiuresisIcon, verb: 'Drains', line: 'Drains a flat amount from every enemy each round, set by the equipped diuretics.' },
};

/* Semantic key -> { type, label, line? }. `label` is the chip's DISPLAY NAME
   (semantic, human) and always leads the chip; the backend's own `label` is a
   noun/unit annotation (e.g. "minis", "rounds", "GI drivers") surfaced as a
   suffix where it adds meaning, never as the lead. `line` is the hover-card
   description (no em dashes -> per house rule). Every key the 10 sim pools emit
   today (dumped 2026-07-13, scratchpad/enemy_status_ground_truth.txt) is
   registered here; unknown future keys still render via inferType() below.

   🔴 THAT CLAIM HAD GONE FALSE AND IS TRUE AGAIN (re-derived from the emitters, not
   from the dump, 2026-08-15). `rebound-trigger`, `withdrawal` and `punctal-counseling`
   are CONDITIONAL payloads — emitted only once a fight reaches a given state — so they
   were absent from the 2026-07-13 dump, were never registered, and inferType() gave all
   three the generic charge chrome plus a hover line that was wrong in one case and
   backwards in another. **An unregistered key does not error. It renders plausibly and
   says the wrong thing**, so re-derive this list from the emitters rather than trusting
   a dated dump. ⚠️ `resistant-clone` (mitos.py:183) is a FOURTH such key, still
   unregistered and deliberately so: it counts total_divisions/2 on an entity that 2
   divisions spawned, so it always reads at or over its own cap. What it should SAY is
   a design question, not a lookup. */
export const ENEMY_STATUS_META = {
  // Gauges (carry a backend `zone`)
  glucose:            { type: 'enemy-gauge', label: 'Glucose', line: 'Blood glucose runs a live gauge; outside the safe band it bites every round.' },
  inr:                { type: 'enemy-gauge', label: 'INR', line: 'Anticoagulation gauge; clots when low, bleeds when high, safe in the therapeutic band.' },
  // ⚠️ Corrected 2026-08-15: this said "the harder the renal drivers hit", which the
  // engine does not do. GFR feeds `player_damage_out_modifier` ONLY (nephra.py:258-291,
  // consumed at simultaneous_combat_engine.py:430-436); every Nephra damage_in_modifier
  // is a constant 1.0 (nephra.py:358-360). Falling GFR blunts YOUR strikes.
  gfr:                { type: 'enemy-gauge', label: 'GFR', line: 'Kidney filtration; the lower it drops, the less damage your strikes do.' },
  'enemy-potassium':  { type: 'enemy-gauge', label: 'Potassium', line: 'Serum potassium drifts each round; outside the safe band it damages you.' },
  iop:                { type: 'enemy-gauge', label: 'IOP', line: 'Intraocular pressure; once it climbs out of the safe band it damages the optic nerve.' },
  lithium:            { type: 'enemy-gauge', label: 'Lithium', line: 'Serum lithium; subtherapeutic below the window, therapeutic inside it, toxic above.' },
  'gestational-week': { type: 'enemy-gauge', label: 'Gestation', line: 'Pregnancy clock advances each round; teratogen windows make some classes unsafe to use.' },
  // Two-sided, like `enemy-potassium`: BOTH ends of the band damage. Saturation drifts DOWN
  // on its own, but a bronchodilator strike can overshoot the target and tip into CO2
  // retention, so "outside the target band" is the accurate phrasing rather than "below".
  'gas-exchange':     { type: 'enemy-gauge', label: 'Gas Exchange', line: 'Oxygen saturation drifts down each round; outside the target band it damages you.' },

  // Charges (a meter climbing toward a threshold event)
  exacerbation:       { type: 'enemy-charge', label: 'Exacerbation', line: 'Charges each round; at the cap it bursts for damage, then resets.' },
  eps:                { type: 'enemy-charge', label: 'EPS', line: 'Each strike builds EPS; at the cap it turns permanent and cuts your output.' },
  uremia:             { type: 'enemy-charge', label: 'Uremia', line: 'Uremic toxins rise each round; dialysis clears them.' },
  accumulation:       { type: 'enemy-charge', label: 'Accumulation', line: 'Renally cleared drug accumulates each round as filtration fails.' },
  // ⚠️ `relieved, never cleared` is load-bearing, not loose phrasing. Obstruction floors at
  // OBSTRUCTION_IRREVERSIBLE_FLOOR (simultaneous_pool/chroni.py) and a bronchodilator strike
  // cannot take it below that, because COPD obstruction does not fully reverse. Pulm's
  // retired `constriction` DID clear to zero and its line said "relieves it". Do not
  // reconcile this line back toward that one.
  obstruction:        { type: 'enemy-charge', label: 'Obstruction', line: 'Airflow obstruction builds each round; a bronchodilator relieves it, never clears it.' },
  'air-trapping':     { type: 'enemy-charge', label: 'Air Trapping', line: 'Trapped air builds each round and cuts your output; a LAMA strike clears one.' },
  veil:               { type: 'enemy-charge', label: 'Dry Eye', line: 'The tear-film veil builds each round and cuts your output.' },
  // ⚠️ Corrected 2026-08-15 on both halves. Resista's `per_round_effect` increments
  // UNCONDITIONALLY every round (glyca.py:180-181) with no reference to the answer or
  // the drug class, so "repeated wrong classes" named a trigger that does not exist;
  // and the only reader multiplies the PLAYER's damage out
  // (simultaneous_combat_engine.py:420, :433), not the enemy's. "Up to half" is
  // RESISTANCE_DECAY_CAP = 0.50 (simultaneous_combat_engine.py:26). "Blunting" is the
  // house verb for this effect (statusTags.jsx tolerance, and `plugs` below).
  resistance:         { type: 'enemy-charge', label: 'Resistance', line: 'Insulin resistance builds each round, blunting your strike damage by up to half.' },
  microvascular:      { type: 'enemy-charge', label: 'Microvascular', line: 'Sustained hyperglycemia stacks microvascular damage that never clears.' },
  'clot-ramp':        { type: 'enemy-charge', label: 'Clot Ramp', line: 'Subtherapeutic anticoagulation ramps the clotting strike each round.' },
  'bleed-ramp':       { type: 'enemy-charge', label: 'Bleed Ramp', line: 'Supratherapeutic anticoagulation ramps the bleeding strike each round.' },
  'ibd-ladder':       { type: 'enemy-charge', label: 'IBD Ladder', line: 'Step-up therapy ladder; each rung raises the flare it can throw.' },
  // ⚠️ Corrected 2026-08-15: it does NOT build each round. `gravida.py:269-287`
  // increments only on an INCORRECT strike carrying a CYP-inducer tag, and the only
  // consumer forces a player loss at the cap (:413-420) rather than weakening a drug.
  'inducer-load':     { type: 'enemy-charge', label: 'Inducer Load', line: 'Wrong inducer answers build enzyme induction; at the cap you lose the fight.' },
  preventive:         { type: 'enemy-charge', label: 'Preventive', line: 'Preventive coverage builds toward suppressing the attack phase.' },
  withdrawal:         { type: 'enemy-charge', label: 'Withdrawal', line: 'Rounds clean of abortive overuse; two in a row clear the rebound headache.' },
  'reversal-tokens':  { type: 'enemy-charge', label: 'Reversal', line: 'Reversal agent tokens bank up for a burst neutralization.' },
  'triple-course':    { type: 'enemy-charge', label: 'Triple Course', line: 'Tracks the multi-class course; incomplete courses breed a resistant spawn.' },
  'anxia-tolerance':  { type: 'enemy-charge', label: 'Tolerance', line: 'Benzodiazepine tolerance builds, blunting your calming strikes.' },
  'anhedrix-escalation': { type: 'enemy-charge', label: 'Escalation', line: 'The longer it lives, the harder its depressive strike lands.' },
  'anxia-escalation': { type: 'enemy-charge', label: 'Escalation', line: 'The longer it lives, the harder its anxiety strike lands.' },
  'sublethal-division': { type: 'enemy-charge', label: 'Sublethal', line: 'Weak strikes that fail to kill push it toward a resistant division.' },
  'living-drivers':   { type: 'enemy-charge', label: 'Drivers', line: 'Living driver population feeding the boss shield.' },
  'living-clones':    { type: 'enemy-charge', label: 'Clones', line: 'Living clone population feeding the boss shield.' },
  diuresis:           { type: 'enemy-diuresis', label: 'Diuresis', line: 'Drains a flat amount from every enemy each round, set by the equipped diuretics.' },

  // Timers (counts down to an enemy event)
  division:           { type: 'enemy-timer', label: 'Divides in', line: 'Splits into a daughter cell when the timer runs out.' },
  // A DECREMENTING window, which is why it is a timer and not a charge. Unregistered
  // it inferred `enemy-charge` off its `max`, so the live rail drew it as an orange
  // lightning meter climbing to "2/2" under "Builds toward a threshold event" — the
  // exact reverse of what it does (visia.py:27, :342-350, :424-458, :586-594).
  'punctal-counseling': { type: 'enemy-timer', label: 'Punctal Occlusion', line: 'Punctal occlusion counseling holds; beta-blocker strikes cost you no systemic damage.' },

  // Spawns (a tracked condition approaching a spawn)
  'resistra-spawn':   { type: 'enemy-spawn', label: 'Resistant strain', line: 'Partial antibiotic courses breed a resistant strain at the cap.' },
  'opportuna-spawn':  { type: 'enemy-spawn', label: 'Opportunist', line: 'Sustained immunosuppression spawns an opportunistic infection.' },
  'cdif-spawn':       { type: 'enemy-spawn', label: 'C. diff', line: 'Consecutive PPI rounds culture a C. diff spawn.' },
  'b12def-spawn':     { type: 'enemy-spawn', label: 'B12 deficiency', line: 'Cumulative PPI exposure spawns a B12-deficiency add.' },
  'rebound-trigger':  { type: 'enemy-spawn', label: 'Rebound headache', line: 'Abortive-class strikes build overuse; the third spawns the rebound headache.' },
  'corpulmo-spawn':   { type: 'enemy-spawn', label: 'Cor pulmonale', line: 'Consecutive hypoxemic rounds spawn cor pulmonale at the third.' },

  // Phases (cycles through named states)
  'migraine-phase':   { type: 'enemy-phase', label: 'Migraine', line: 'Cycles dormant -> prodrome -> attack.' },

  // Match (only the right therapeutic class produces the effect)
  etiology:           { type: 'enemy-match', label: 'Etiology', line: 'Match the etiology to clear it; the wrong class costs you damage.' },
  'hepatotoxic-match': { type: 'enemy-match', label: 'Hepatotoxic', line: 'Hepatotoxic classes feed it; avoid them or it heals off your strike.' },
  // ⚠️ Corrected 2026-08-15: it fires on a WRONG answer, not on "hitting it with a
  // matching offender" (hemora.py:325-348 gates on `on_incorrect_strike` whose tags
  // intersect DDI_TAGS). No multiple is stated on purpose: `delta = 2 * last_delta` is
  // re-applied ON TOP of the current INR, so neither "doubles" nor "triples" is true.
  'ddi-amplifier':    { type: 'enemy-match', label: 'DDI', line: 'Carries interaction tags; a wrong answer on one amplifies the last INR swing.' },

  // Shields (incoming damage is zero while the stated condition holds)
  'mini-shield':      { type: 'enemy-shield', label: 'Shielded', line: 'Takes no damage while its driver minis are alive.' },
  'onset-shield':     { type: 'enemy-shield', label: 'Withholding', line: 'Takes no damage while its delayed strikes are still queued.' },

  // Resistant AURA — a flat regen the enemy heals back each round. The boss keeps
  // it; every enemy gains it after two dispenses (Edward 2026-07-14).
  resistant:          { type: 'enemy-rebuild', label: 'Resistant', line: 'Regenerates HP each round (an aura). The boss keeps it; other enemies gain it after two dispenses.' },
};

// Infer a renderer type for a key we don't have an explicit entry for, from the
// payload shape. Keeps the rail rendering even as the backend key set evolves.
function inferType(status) {
  const key = String(status.key || '').toLowerCase();
  if (typeof status.value === 'boolean' || key.includes('shield') || key.includes('withhold')) return 'enemy-shield';
  if (status.zone != null) return 'enemy-gauge';
  if (key.includes('phase') || key.includes('cycle')) return 'enemy-phase';
  if (key.includes('spawn') || key.includes('rebound') || key.includes('trigger')) return 'enemy-spawn';
  if (key.includes('division') || key.includes('divide') || key.includes('timer') || key.includes('onset') || key.includes('countdown')) return 'enemy-timer';
  if (key.includes('match') || key.includes('etiology') || key.includes('class')) return 'enemy-match';
  if (status.max != null) return 'enemy-charge';
  return 'enemy-charge';
}

/* Resolve one backend payload row to everything the chip needs to render.
   `label` (chip name) is our semantic display name — NEVER the backend `label`,
   which is a noun/unit annotation carried separately so formatValue can suffix
   it where it reads well ("Shielded · 4 minis") instead of leading with it. */
// `semantic` defaults to the registry entry. A caller drawing a status the registry does
// not hold yet (decode.jsx's optional extra vocabulary) passes that entry instead; it
// takes the same `{ type, label, line }` shape and the same type chrome.
export function resolveEnemyStatus(status, semantic = ENEMY_STATUS_META[status?.key]) {
  if (!status || status.key == null) return null;
  const type = semantic?.type || inferType(status);
  const typeMeta = ENEMY_STATUS_TYPES[type] || ENEMY_STATUS_TYPES['enemy-charge'];
  const name = semantic?.label || prettifyKey(status.key);
  const annotation = status.label || null;
  return {
    key: status.key,
    type,
    statusClass: typeMeta.statusClass,
    color: typeMeta.color,
    Icon: typeMeta.Icon,
    verb: typeMeta.verb,
    line: semantic?.line || typeMeta.line,
    label: name,
    annotation,
    zone: status.zone || null,
    valueText: formatValue(type, { ...status, name, annotation }),
  };
}

function prettifyKey(key) {
  return String(key || '').replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// The on-chip text after the icon. Type-specific so a gauge reads "Glucose 190",
// a charge "Exacerbation 1/2", a shield "Shielded · 4 minis". Zone is appended
// by the chip for gauges. `name` leads; `annotation` (backend label) suffixes.
function formatValue(type, status) {
  const { name, value, max, annotation } = status;
  switch (type) {
    case 'enemy-gauge':
      return `${name} ${fmtNum(value)}`;
    case 'enemy-charge':
    case 'enemy-diuresis':
    case 'enemy-spawn':
      return max != null ? `${name} ${fmtNum(value)}/${fmtNum(max)}` : `${name} ${fmtNum(value)}`;
    case 'enemy-timer': {
      const n = Number(value);
      const unit = Math.abs(n) === 1 ? 'round' : 'rounds';
      return `${name} ${fmtNum(value)} ${unit}`;
    }
    case 'enemy-phase':
      return `${name} ${String(value ?? '').toLowerCase()}`.trim();
    case 'enemy-shield':
      // The driver count is the point of a shield — surface it with the backend
      // annotation ("4 minis", "1 queued"). Boolean shields just name the state.
      if (typeof value === 'boolean') return name;
      if (value != null && annotation) return `${name} · ${fmtNum(value)} ${annotation}`;
      return value != null ? `${name} ${fmtNum(value)}` : name;
    case 'enemy-match':
      if (typeof value === 'boolean') return annotation ? `${name} · ${annotation}` : name;
      return value != null ? `${name} ${value}` : name;
    case 'enemy-rebuild':
      // Resistant is a FLAT, always-on aura (+5/round, the sole rebuild status), so the
      // number is implied and reads as noise on the live rail — the chip just names the
      // state ("Resistant"). The magnitude still lives on the Ten Shin teaching card's
      // "Develops [resistant] +5 / round" row, its source of truth (Edward 2026-07-14).
      return name;
    default:
      return `${name} ${fmtNum(value)}`;
  }
}

function fmtNum(v) {
  if (v == null) return '';
  if (typeof v === 'number' && !Number.isInteger(v)) return String(Math.round(v * 10) / 10);
  return String(v);
}
